import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Stack, Box } from "@mui/material";
import { DEFAULT_LOCALE, getLocalizedPath, Locale, setLanguageInStorage, getStoredLocaleFromStorage, getLocaleFromUrl, setupLocaleStorageListener, validateLocale } from "../utils/routing";
import i18n from "../i18n";
import Routers from "./main";
import StickyLanguageBar from "../components/StickyLanguageBar";
import { ROUTE_PATHS } from "../constants";
import NavigationGuardDialog from "../components/NavigationGuardDialog";
import useGlobalNavigationGuard from "../hooks/useGlobalNavigationGuard";

const LocaleRouter = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // The navigation guard (history trap + swipe/back-button dialog) must only be
  // active on authenticated shell pages. Enabling it on auth/public pages
  // (login, OTP, forgot-password, …) causes those URLs to be pushed into the
  // browser history stack via pushState, making Back return to them after login.
  const isProtectedShellRoute =
    location.pathname.includes(ROUTE_PATHS.DASHBOARD) ||
    location.pathname.includes(ROUTE_PATHS.PROFILE) ||
    location.pathname.includes(ROUTE_PATHS.HOME);

  const navGuard = useGlobalNavigationGuard({ enabled: isProtectedShellRoute });
  
  // Debug: Log location changes
  useEffect(() => {
    console.log("🔵 LocaleRouter: Location changed", {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      fullPath: `${location.pathname}${location.search}${location.hash}`,
    });
  }, [location]);

  // Monitor localStorage for external changes to lnc-i18n (e.g., from repo)
  useEffect(() => {
    const cleanup = setupLocaleStorageListener((locale: Locale) => {
      // When lnc-i18n is changed externally, validate and sync
      const urlLocale = getLocaleFromUrl(location.pathname);
      
      // If URL has locale, URL takes priority - don't change
      if (urlLocale) {
        // URL has priority, but ensure localStorage is synced
        setLanguageInStorage(urlLocale);
        if (i18n.language !== urlLocale) {
          i18n.changeLanguage(urlLocale);
        }
        return;
      }
      
      // No URL locale, so use the externally set locale
      if (i18n.language !== locale) {
        i18n.changeLanguage(locale);
      }
      
      // Update URL to match the new locale, preserving search parameters (query string)
      const pathSegments = location.pathname.split("/").filter(Boolean);
      const pathWithoutLocale = pathSegments.join("/").replace(/^(en|es)\//, "") || "login";
      const newPath = getLocalizedPath(pathWithoutLocale, locale);
      const newPathWithSearch = location.search ? `${newPath}${location.search}` : newPath;
      console.log("🟢 LocaleRouter: Updating URL from localStorage change", {
        originalPath: location.pathname,
        originalSearch: location.search,
        newPath,
        newPathWithSearch,
        locale,
      });
      navigate(newPathWithSearch, { replace: true });
    });
    
    return cleanup;
  }, [location.pathname, location.search, navigate]);

  // Validate and sync locale when URL or pathname changes
  useEffect(() => {
    const pathname = location.pathname;
    
    // Priority: URL (highest) > localStorage > default
    
    // Handle root path (basename is /user/, so pathname will be / or empty)
    if (pathname === "/" || pathname === "") {
      // No URL locale, use stored locale from localStorage or default
      const storedLocale = getStoredLocaleFromStorage();
      setLanguageInStorage(storedLocale);
      i18n.changeLanguage(storedLocale);
      const newPath = getLocalizedPath("login", storedLocale);
      const newPathWithSearch = location.search ? `${newPath}${location.search}` : newPath;
      console.log("🟢 LocaleRouter: Redirecting root path", {
        originalPath: pathname,
        originalSearch: location.search,
        newPath,
        newPathWithSearch,
        storedLocale,
      });
      navigate(newPathWithSearch, { replace: true });
      return;
    }
    
    // Check if locale is in URL (pathname will be like /en/login or /es/login)
    const urlLocale = getLocaleFromUrl(pathname);
    const storedLocale = getStoredLocaleFromStorage();
    const pathSegments = pathname.split("/").filter(Boolean);

    // Priority 1: URL locale (highest priority)
    if (urlLocale) {
      // urlLocale is already validated by getLocaleFromUrl
      // URL has valid locale - use it and update localStorage
      if (i18n.language !== urlLocale) {
        i18n.changeLanguage(urlLocale);
      }
      // Always update localStorage when URL locale is present (even if it matches)
      // This ensures localStorage is synced with URL when URL is copied to new browser
      setLanguageInStorage(urlLocale);
      return;
    }

    // Priority 2: No URL locale, use stored locale from localStorage
    // If no locale or invalid locale in URL, use stored locale and update URL
    if (!urlLocale) {
      // Validate stored locale
      const validatedStoredLocale = validateLocale(storedLocale) || DEFAULT_LOCALE;
      
      // Use stored locale from localStorage (or default if invalid)
      setLanguageInStorage(validatedStoredLocale);
      i18n.changeLanguage(validatedStoredLocale);
      
      // Get the path without locale prefix
      const pathWithoutLocale = pathSegments.join("/") || "login";
      
      // Update URL to include locale, preserving search parameters (query string)
      const newPath = getLocalizedPath(pathWithoutLocale, validatedStoredLocale);
      const newPathWithSearch = location.search ? `${newPath}${location.search}` : newPath;
      console.log("🟢 LocaleRouter: Redirecting to add locale", {
        originalPath: pathname,
        originalSearch: location.search,
        newPath,
        newPathWithSearch,
        validatedStoredLocale,
      });
      navigate(newPathWithSearch, { replace: true });
      return;
    }
  }, [location.pathname, location.search, navigate, i18n]);

  // IamShell (common layout) provides its own language selector in the header.
  // Hide StickyLanguageBar on all routes rendered inside IamShell so the two
  // selectors don't overlap.
  const isIamShellRoute = isProtectedShellRoute;
  // Hide language selector on sign-up, user-setup, and account-setup pages
  const isSignUpPage = location.pathname.includes(ROUTE_PATHS.SIGNUP);
  const isUserSetupPage = location.pathname.includes(ROUTE_PATHS.USER_SETUP);
  const isAccountSetupPage = location.pathname.includes(ROUTE_PATHS.ACCOUNT_SETUP);

  return (
    <Stack sx={{ position: "relative", width: "100%", height: "100%" }}>
      {!isIamShellRoute && !isSignUpPage && !isUserSetupPage && !isAccountSetupPage && <StickyLanguageBar />}
      <Box sx={{ position: "relative", zIndex: 10, width: "100%", height: "100%" }}>
        <Routers />
      </Box>
      <NavigationGuardDialog
        isOpen={navGuard.isOpen}
        onConfirm={navGuard.onConfirm}
        onCancel={navGuard.onCancel}
      />
    </Stack>
  );
};

export default LocaleRouter;

